/** @file utils and types for normalizing commands from the API and UI to a consistent format */

import {normalizeSequence, type NormalizedSequenceString} from '@github-ui/hotkey'
import type {APICommand, APICommandGroup, APIShortcuts} from './keyboard-shortcuts-query-options'
import {
  GLOBAL_NAVIGATION_SERVICE_ID,
  UI_COMMANDS_SERVICE_ID,
  type RegisteredUICommand,
  type RegisteredUICommandGroup,
} from '@github-ui/ui-commands/internal'
import strings from './strings'

export type NormalizedApiCommand = APICommand & {keybinding: NormalizedSequenceString | NormalizedSequenceString[]}

export type NormalizedCommand = RegisteredUICommand | NormalizedApiCommand

export type NormalizedGroup = {service: {id: string; name: string}; commands: NormalizedCommand[]}

export interface NormalizedShortcuts {
  siteWideShortcuts: NormalizedGroup
  shortcutGroups: NormalizedGroup[]
}

function normalizeAPICommand(command: APICommand): NormalizedApiCommand {
  const {keybinding} = command

  if (Array.isArray(keybinding)) {
    return {
      ...command,
      keybinding: keybinding.map(kb => normalizeSequence(kb)),
    }
  }

  return {
    ...command,
    keybinding: normalizeSequence(keybinding ?? ''),
  }
}

function normalizeAPIGroup(group: APICommandGroup): NormalizedGroup {
  return {
    service: group.service,
    commands: group.commands.map(normalizeAPICommand),
  }
}

function combineMatchingGroups(groups: NormalizedGroup[]): NormalizedGroup[] {
  const groupMap = new Map<string, NormalizedGroup>()

  for (const group of groups) {
    const existing = groupMap.get(group.service.id)
    if (existing) {
      existing.commands = existing.commands.concat(group.commands)
    } else {
      groupMap.set(group.service.id, {...group})
    }
  }

  return Array.from(groupMap.values())
}

export function normalizeShortcuts(
  registeredUiCommandGroups: RegisteredUICommandGroup[],
  apiShortcuts: APIShortcuts | null,
) {
  const globalUiCommandsGroup = registeredUiCommandGroups.find(
    group => group.service.id === GLOBAL_NAVIGATION_SERVICE_ID,
  )

  const nonGlobalUiCommandsGroups = registeredUiCommandGroups.filter(
    // The ui-commands service just provides generic utility commands which don't need to be documented
    group => group.service.id !== GLOBAL_NAVIGATION_SERVICE_ID && group.service.id !== UI_COMMANDS_SERVICE_ID,
  )

  // UI Commands are already normalized, no need to transform them further
  if (!apiShortcuts) {
    return {
      siteWideShortcuts: {
        service: {id: 'global', name: strings.siteWideShortcuts},
        commands: globalUiCommandsGroup?.commands ?? [],
      },
      shortcutGroups: nonGlobalUiCommandsGroups,
    }
  }

  const {global: globalApiGroup, ...nonGlobalApiGroups} = apiShortcuts.commands

  return {
    siteWideShortcuts: {
      service: {id: 'global', name: strings.siteWideShortcuts},
      commands: [
        ...(globalApiGroup?.commands.map(normalizeAPICommand) ?? []),
        ...(globalUiCommandsGroup?.commands ?? []),
      ],
    },
    shortcutGroups: combineMatchingGroups([
      ...Object.values(nonGlobalApiGroups).map(normalizeAPIGroup),
      ...nonGlobalUiCommandsGroups,
    ]),
  }
}
